/**
 * Minimal STORE-method (uncompressed) ZIP writer. Enough to pack a handful
 * of text files for backup downloads without pulling in jszip. Files are
 * added in order via addFile() and a single `Blob` is produced by build().
 *
 * Format reference: https://pkware.cachefly.net/webdocs/APPNOTE/APPNOTE-6.3.9.TXT
 * Only the bare minimum fields are written:
 *   - Local file header (sig 0x04034b50)
 *   - File data
 *   - Central directory file header (sig 0x02014b50)
 *   - End of central directory record (sig 0x06054b50)
 *
 * Method = 0 (STORE), compressed size === uncompressed size, CRC-32 computed
 * via a cached lookup table. No compression, no zip64.
 */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) {
      c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[i] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

function utf8(str) {
  return new TextEncoder().encode(str);
}

// DOS date/time encoding for zip headers.
function dosDateTime(date) {
  const d = date || new Date();
  const dosTime =
    ((d.getHours() & 0x1f) << 11) |
    ((d.getMinutes() & 0x3f) << 5) |
    ((Math.floor(d.getSeconds() / 2)) & 0x1f);
  const dosDate =
    (((d.getFullYear() - 1980) & 0x7f) << 9) |
    (((d.getMonth() + 1) & 0x0f) << 5) |
    (d.getDate() & 0x1f);
  return { dosDate, dosTime };
}

function writeU16LE(view, offset, value) {
  view.setUint16(offset, value, true);
}
function writeU32LE(view, offset, value) {
  view.setUint32(offset, value >>> 0, true);
}

export function createMiniZip() {
  const files = []; // { nameBytes, dataBytes, crc, dosDate, dosTime, localHeaderOffset }

  return {
    /**
     * Add a text file to the archive. `content` is any string.
     */
    addTextFile(path, content, date) {
      const nameBytes = utf8(path);
      const dataBytes = utf8(content == null ? '' : String(content));
      const crc = crc32(dataBytes);
      const { dosDate, dosTime } = dosDateTime(date);
      files.push({ nameBytes, dataBytes, crc, dosDate, dosTime, localHeaderOffset: 0 });
    },
    /**
     * Finalize and return a Blob for download.
     */
    build() {
      // Pass 1: compute local header offsets + total size
      let cursor = 0;
      const localChunks = [];
      for (const f of files) {
        f.localHeaderOffset = cursor;
        const headerSize = 30 + f.nameBytes.length;
        const header = new Uint8Array(headerSize);
        const view = new DataView(header.buffer);
        writeU32LE(view, 0, 0x04034b50);       // local file header sig
        writeU16LE(view, 4, 20);                // version needed
        writeU16LE(view, 6, 0x0800);            // flags (UTF-8 filename)
        writeU16LE(view, 8, 0);                 // method (STORE)
        writeU16LE(view, 10, f.dosTime);
        writeU16LE(view, 12, f.dosDate);
        writeU32LE(view, 14, f.crc);
        writeU32LE(view, 18, f.dataBytes.length); // compressed size
        writeU32LE(view, 22, f.dataBytes.length); // uncompressed size
        writeU16LE(view, 26, f.nameBytes.length); // filename length
        writeU16LE(view, 28, 0);                  // extra field length
        header.set(f.nameBytes, 30);
        localChunks.push(header);
        localChunks.push(f.dataBytes);
        cursor += headerSize + f.dataBytes.length;
      }

      // Pass 2: central directory
      const centralStart = cursor;
      const centralChunks = [];
      for (const f of files) {
        const entrySize = 46 + f.nameBytes.length;
        const entry = new Uint8Array(entrySize);
        const view = new DataView(entry.buffer);
        writeU32LE(view, 0, 0x02014b50);        // central dir sig
        writeU16LE(view, 4, 20);                 // version made by
        writeU16LE(view, 6, 20);                 // version needed
        writeU16LE(view, 8, 0x0800);             // flags
        writeU16LE(view, 10, 0);                 // method
        writeU16LE(view, 12, f.dosTime);
        writeU16LE(view, 14, f.dosDate);
        writeU32LE(view, 16, f.crc);
        writeU32LE(view, 20, f.dataBytes.length);
        writeU32LE(view, 24, f.dataBytes.length);
        writeU16LE(view, 28, f.nameBytes.length);
        writeU16LE(view, 30, 0);                 // extra
        writeU16LE(view, 32, 0);                 // comment
        writeU16LE(view, 34, 0);                 // disk number
        writeU16LE(view, 36, 0);                 // internal attrs
        writeU32LE(view, 38, 0);                 // external attrs
        writeU32LE(view, 42, f.localHeaderOffset);
        entry.set(f.nameBytes, 46);
        centralChunks.push(entry);
        cursor += entrySize;
      }
      const centralSize = cursor - centralStart;

      // End of central directory record
      const eocd = new Uint8Array(22);
      const eocdView = new DataView(eocd.buffer);
      writeU32LE(eocdView, 0, 0x06054b50);
      writeU16LE(eocdView, 4, 0);             // disk number
      writeU16LE(eocdView, 6, 0);             // disk with CD
      writeU16LE(eocdView, 8, files.length);  // total entries on this disk
      writeU16LE(eocdView, 10, files.length); // total entries
      writeU32LE(eocdView, 12, centralSize);
      writeU32LE(eocdView, 16, centralStart);
      writeU16LE(eocdView, 20, 0);            // comment length

      return new Blob([...localChunks, ...centralChunks, eocd], { type: 'application/zip' });
    }
  };
}

/**
 * Convenience helper: trigger a browser download for a Blob under the given
 * filename. No-op on the server side (e.g. in SSR).
 */
export function downloadBlob(blob, filename) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}
